<template>
  <v-card flat>
    <v-card-title class="text-h5">
      Exploratory Regression Analysis
    </v-card-title>
    <v-card-subtitle>
      This module allows the user to create a Multiple Regression Model to predict future deforestation. In the panel of the left the user can enable/disable the variables and distance used to compute the regression model. Together with the regression model, the tool provides AIC results to identify the statistically best combination of variables.
      <br/>The user can repeat the process as many times as needed until the regression model is robust enough.
      <br/>Each time there is a modification in the selected variables, the regression model must be re-calculated by clicking the “Process” button.
      <br/>Once you are satisfied with the result of the multiple regression model, go to Module 4. Otherwise, modify the selected variables and parameters to process the regression again.
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <!-- Input form -->
        <v-col sm="4">
          <v-card elevation="4">
            <v-card-text>
              <v-form v-model="isValid">
                <!-- Mod2 Variables -->
                <p class="text-body-1 pt-4">Select the variables and distance to be used</p>
                <v-card class="ma-2" elevation="4" v-for="(item, i) in mod2Variables" :key="item.Variable">
                  <v-card-title>
                    <v-switch class="mt-0" v-model="item.use" :label="item.Variable" hide-details/>
                  </v-card-title>

                  <v-expand-transition>
                    <v-card-text class="pt-3" v-show="item.use">
                      <v-slider class="pt-6" v-if="!isSliderMultiple[i]" v-model="item.Distance" step="500" min="0" max="5000" thumb-label="always" ticks="always" tick-size="4" :rules="getMod2VarsRules(item, i)"/>
                      <v-range-slider class="pt-6" v-else v-model="item.range" step="500" min="0" max="5000" thumb-label="always" ticks="always" tick-size="4" :rules="getMod2VarsRules(item, i)"/>

                      <v-checkbox v-model="isSliderMultiple[i]" label="Use two distances" hide-details/>
                    </v-card-text>
                  </v-expand-transition>
                </v-card>

                <!-- Continuous variables -->
                <p class="text-body-1 pt-4">Continuous variables</p>
                <p class="text-body-1 pt-4">User can include continuous variables and specific criteria into the analysis.</p>
                <v-card class="ma-2" elevation="5" v-for="(item, i) in continuousVars" :key="i">
                  <v-card-title>
                    <v-switch class="mt-0" v-model="item.enabled" :label="item.name" hide-details/>
                  </v-card-title>

                  <v-expand-transition>
                    <v-card-text class="pt-3" v-show="item.enabled">
                      <v-row>
                        <v-col>
                          <v-text-field v-model="item.value[0]" class="right-input" v-show="!item.useAverage" label="Value" :min="0" outlined dense type="number" :rules="getContVarRules(item)"/>
                        </v-col>
                        <v-col>
                          <v-text-field v-model="item.value[1]" class="right-input" v-show="!item.useAverage" :disabled="!item.useMultipleValues" label="Value 2" :min="0" outlined dense type="number" :rules="getContVarRules(item)"/>
                        </v-col>
                      </v-row>

                      <v-radio-group class="mt-0" v-if="!item.useMultipleValues" v-model="item.direction" row hide-details>
                        <v-radio :value="0" :label="`<= ${item.useAverage ? 'Average' : item.value[0]}`"/>
                        <v-radio :value="1" :label="`>= ${item.useAverage ? 'Average' : item.value[0]}`"/>
                        <v-checkbox class="mt-0 pt-0" v-model="item.useAverage" label="Average" hide-details/>
                      </v-radio-group>

                      <v-checkbox v-model="item.useMultipleValues" v-show="!item.useAverage" label="Use two values" hide-details/>
                    </v-card-text>
                  </v-expand-transition>
                </v-card>
              </v-form>

              <v-btn class="mt-4 ml-2" width="150" color="primary" :disabled="!isValid || !areVariablesValid" :loading="isInitProcessing" @click="process">Process</v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Results -->
        <v-col>
          <v-card elevation="4">
            <v-card-title>The multiple regression model derived from the variables and distances selected is:</v-card-title>

            <v-card-text>
              <v-textarea v-model="regressionSummary" v-if="!!regressionSummary" readonly filled height="570px" class="text-body-1" no-resize/>

              <br/>
              Download the AIC report to identify the best combination of variables.
            </v-card-text>

            <v-card-actions class="px-4">
              <v-btn color="primary" outlined @click="downloadFile(aicFile)" :disabled="!aicFile">
                <v-icon left>mdi-download</v-icon>
                Download AIC
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import api from '../../api'

export default {
  name: 'Module3',

  props: {
    region: Object,
    currentProcess: String
    // mod2Variables: Array
  },

  data: () => ({
    isValid: true,

    continuousVars: [
      {
        name: 'Population Density',
        layer: null,
        enabled: false,
        value: [1, 25],
        useAverage: false,
        useMultipleValues: false,
        direction: 0
      },
      {
        name: 'Digital Elevation Model',
        layer: null,
        enabled: false,
        value: [1, 25],
        useAverage: false,
        useMultipleValues: false,
        direction: 0
      },
      {
        name: 'Aspect',
        layer: null,
        enabled: false,
        value: [1, 25],
        useAverage: false,
        useMultipleValues: false,
        direction: 0
      },
      {
        name: 'Slope',
        layer: null,
        enabled: false,
        value: [1, 25],
        useAverage: false,
        useMultipleValues: false,
        direction: 0
      }
    ],

    // Periodic checking
    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    finishedProcessing: false,

    // Input
    mod2Variables: [],
    range: [100, 3500],
    isSliderMultiple: [],

    // Output
    regressionSummary: null,
    aicFile: null
  }),

  watch: {
    isProcessing (val) {
      this.isInitProcessing = val

      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          this.getResultsFromModule(3).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                this.regressionSummary = response.data.results.modelSummary
                this.aicFile = response.data.results.aicZip
                // console.log(this.aicFile)
                // Save results in store
                this.setMod3Results({
                  regressionSummary: this.regressionSummary,
                  aicFile: this.aicFile
                })

                this.$toastr.s('Module 3 succesfully processed')

                // Ready for module 4
                this.finishedProcessing = true
                this.$emit('onProcessed')

                this.stopProcessing()
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing module 3')
          }).finally(() => {
            // Nothing
          })
        }, 10 * 1000)
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  computed: {
    ...mapGetters('file', [
      'files'
    ]),

    areVariablesValid () {
      let variablesValid = false
      for (const continuousVar of this.continuousVars) {
        if (continuousVar.enabled) {
          variablesValid = true
        }
      }
      for (const mod2Var of this.mod2Variables) {
        if (mod2Var.use) {
          variablesValid = true
        }
      }
      return variablesValid
    }
  },

  methods: {
    ...mapActions('app', [
      'setIsProcessing'
    ]),

    ...mapActions('currentProcess', [
      'setMod3Results'
    ]),

    ...mapActions('file', [
      'downloadFile'
    ]),

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    reset () {
      this.finishedProcessing = false

      this.isValid = true
      for (let i = 0; i < this.continuousVars.length; i++) {
        this.continuousVars[i].layer = null
        this.continuousVars[i].enabled = false
        this.continuousVars[i].value = [1, 25]
        this.continuousVars[i].useAverage = false
        this.continuousVars[i].useMultipleValues = false
        this.continuousVars[i].direction = 0
      }

      // Input
      this.mod2Variables = []
      this.range = [100, 3500]
      this.isSliderMultiple = []

      // Output
      this.regressionSummary = null
      this.aicFile = null
    },

    getMod2VarsRules (variable, index) {
      let rules = []

      if (this.isSliderMultiple[index]) { // Both values > 0
        rules.push(v => (v && v[0] > 0 && v[1] > 0) || 'Both values should be greater than 0')
      } else { // Single value > 0
        if (variable.use) {
          rules.push(v => (v && v > 0) || 'Value should be greater than 0')
        }
      }

      return rules
    },

    getContVarRules (variable) {
      let rules = [
        v => !!v || 'This field is required',
        v => (v && v >= 0) || 'Value should be at least 0'
      ]

      if (variable.name === 'Slope') {
        rules.push(v => (v && v <= 100) || 'Slope should not be above 100')
      } else if (variable.name === 'Aspect') {
        rules.push(v => (v && v <= 360) || 'Aspect should not be above 360')
      }

      return rules
    },

    initMod2Variables (mod2Vars) {
      this.mod2Variables = mod2Vars
    },

    async process () {
      for (let i = 0; i < this.mod2Variables.length; i++) {
        if (this.isSliderMultiple[i]) {
          this.mod2Variables[i].Distance = this.mod2Variables[i].range[0]
          this.mod2Variables[i].Distance2 = this.mod2Variables[i].range[1]
        }
      }

      for (let i = 0; i < this.continuousVars.length; i++) {
        this.continuousVars[i].value[0] = parseFloat(this.continuousVars[i].value[0])
        this.continuousVars[i].value[1] = parseFloat(this.continuousVars[i].value[1])
        if (this.continuousVars[i].enabled && this.continuousVars[i].useMultipleValues) {
          if (parseFloat(this.continuousVars[i].value[0]) > parseFloat(this.continuousVars[i].value[1]) && i !== 2) {
            let aux = this.continuousVars[i].value[0]
            this.continuousVars[i].value[0] = this.continuousVars[i].value[1]
            this.continuousVars[i].value[1] = aux
          }
        }
      }

      let data = {
        processId: this.currentProcess,
        mod2VariablesRecommended: this.mod2Variables,
        continuousVariables: this.continuousVars
      }

      try {
        this.setIsProcessing(true)
        this.isInitProcessing = true

        await api.process.module3(data)

        this.isProcessing = true
        this.$toastr.s('Module 3 starts processing...')
      } catch (error) {
        this.isInitProcessing = false

        console.error(error)
        this.$toastr.e('Error initializing module 3')
      }
    },

    async getResultsFromModule (numModule) {
      return await api.process.getResults(this.currentProcess, numModule)
    },

    async getMod2Variables () {
      try {
        let response = await this.getResultsFromModule(2)
        this.mod2Variables = response.data.results.mod2Variables
      } catch (error) {
        console.error(error)
      }
    }
  },

  mounted () { // TODO: only for dev purposes
    // this.getMod2Variables()
    for (let i = 0; i < this.mod2Variables.length; i++) {
      i = i + 0
      this.isSliderMultiple.push(false)
    }
  }
}
</script>

<style scoped>
  .right-input >>> input {
          text-align: right
  }
</style>
